<template>
  <div>
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#427D2D" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color:#fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color:#fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <div class="full-screen-map-wrapper">
      <BaseLeafletMap
        title="All Map Markers"
        :layers="mappedLayers"
        :statistics="mapStats"
        :loading="appLoading"
        :toolbar="{ search: true, fullscreen: true, fitBounds: true, satellite: true }"
        height="100vh"
        @marker-click="onMarkerClick"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import BaseLeafletMap from '@/components/maps/BaseLeafletMap.vue';
import UserPopup from '@/components/maps/popups/UserPopup.vue';
import RescuerPopup from '@/components/maps/popups/RescuerPopup.vue';
import HospitalPopup from '@/components/maps/popups/HospitalPopup.vue';
import SnakeReportPopup from '@/components/maps/popups/SnakeReportPopup.vue';
import { layerFactory } from '@/components/maps/utils/layerFactory';
import { LAYER_TYPES } from '@/components/maps/constants/layerTypes';
import { STATUS_COLORS, getRescuerColor } from '@/components/maps/constants/statusColors';
import { createMarkerIcon } from '@/components/maps/utils/markerFactory';
import { MARKER_TYPES } from '@/components/maps/constants/markerTypes';

export default {
  components: { BaseLeafletMap },

  data() {
    return {
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      
      users: [],
      rescuers: [],
      hospitals: [],
      snakeReports: [],
      
      usersCount: 0,
      rescuersCount: 0,
      hospitalsCount: 0,
      snakeReportsCount: 0,
    };
  },

  computed: {
    mappedLayers() {
      const layers = [];

      // 1. Users Layer
      layers.push(layerFactory({
        id: LAYER_TYPES.USERS,
        title: 'Users',
        color: STATUS_COLORS.USER,
        clustered: true,
        searchable: true,
        legend: [{ color: STATUS_COLORS.USER, label: 'App User' }],
        markers: (() => {
          const rawMarkers = this.users
            .filter(u => u.location && u.location.length === 2)
            .map(u => ({
              id: u._id,
              lat: u.location[1],
              lng: u.location[0],
              layer: LAYER_TYPES.USERS,
              popup: UserPopup,
              searchFields: [u.name, u.phone, u.district, u.email].filter(Boolean),
              data: u,
            }));

          const locGroups = {};
          rawMarkers.forEach(m => {
            const key = `${m.lat},${m.lng}`;
            if (!locGroups[key]) locGroups[key] = [];
            locGroups[key].push(m);
          });
          
          const finalMarkers = [];
          Object.values(locGroups).forEach(group => {
            if (group.length === 1) {
              finalMarkers.push(group[0]);
            } else {
              const radius = 0.00015 + (group.length * 0.000005);
              group.forEach((m, i) => {
                const angle = (i / group.length) * 2 * Math.PI;
                m.lat += Math.cos(angle) * radius;
                m.lng += Math.sin(angle) * radius;
                finalMarkers.push(m);
              });
            }
          });
          return finalMarkers;
        })(),
      }));

      // 2. Rescuers Layer
      layers.push(layerFactory({
        id: LAYER_TYPES.RESCUERS,
        title: 'Rescuers',
        color: STATUS_COLORS.AVAILABLE,
        clustered: false,
        searchable: true,
        legend: [
          { color: STATUS_COLORS.AVAILABLE, label: 'Available' },
          { color: STATUS_COLORS.BUSY, label: 'Busy' },
          { color: STATUS_COLORS.OFFLINE, label: 'Offline' },
        ],
        markers: this.rescuers
          .filter(u => u.location && u.location.length === 2)
          .map(u => {
            let status = 'offline';
            if (typeof u.status === 'string') status = u.status.toLowerCase();
            else if (typeof u.availability === 'string') status = u.availability.toLowerCase();
            else if (u.is_rescuer_Available === true) status = 'available';
            else if (u.is_rescuer_Available === false) status = 'busy';
            
            const color = getRescuerColor(status);
            return {
              id: u._id,
              lat: u.location[1],
              lng: u.location[0],
              layer: LAYER_TYPES.RESCUERS,
              status,
              icon: createMarkerIcon(MARKER_TYPES.RESCUER, color),
              popup: RescuerPopup,
              searchFields: [u.name, u.phone, u.district, u.email].filter(Boolean),
              data: u,
            };
          }),
      }));

      // 3. Hospitals Layer
      layers.push(layerFactory({
        id: LAYER_TYPES.HOSPITALS,
        title: 'Hospitals',
        color: STATUS_COLORS.HOSPITAL,
        clustered: false,
        searchable: true,
        legend: [{ color: STATUS_COLORS.HOSPITAL, label: 'Hospital' }],
        markers: this.hospitals
          .filter(h => h.location && h.location.length === 2)
          .map(h => ({
            id: h._id,
            lat: h.location[1],
            lng: h.location[0],
            layer: LAYER_TYPES.HOSPITALS,
            popup: HospitalPopup,
            searchFields: [h.name, h.district, h.address].filter(Boolean),
            data: h,
          })),
      }));

      // 4. Snake Reports Layer
      layers.push(layerFactory({
        id: LAYER_TYPES.SNAKE_REPORTS,
        title: 'Snake Reports',
        color: STATUS_COLORS.PENDING,
        clustered: false,
        searchable: true,
        legend: [
          { color: STATUS_COLORS.PENDING, label: 'Pending' },
          { color: STATUS_COLORS.RESCUED, label: 'Rescued' },
          { color: STATUS_COLORS.RELEASED, label: 'Released' },
          { color: STATUS_COLORS.HANDED_OVER, label: 'Handed Over' },
          { color: STATUS_COLORS.CANCELLED, label: 'Cancelled' },
        ],
        markers: (() => {
          const rawMarkers = this.snakeReports
            .filter(r => r.location && r.location.length === 2)
            .map(r => {
            const status = r.situation || r.status || 'pending';
            let color = STATUS_COLORS.PENDING;
            const s = status.toLowerCase();
            if (s.includes('rescued')) color = STATUS_COLORS.RESCUED;
            else if (s.includes('released')) color = STATUS_COLORS.RELEASED;
            else if (s.includes('handed')) color = STATUS_COLORS.HANDED_OVER;
            else if (s.includes('cancel')) color = STATUS_COLORS.CANCELLED;

            return {
              id: r._id,
              lat: r.location[1],
              lng: r.location[0],
              layer: LAYER_TYPES.SNAKE_REPORTS,
              status,
              popup: SnakeReportPopup,
              searchFields: [
                r.rescue && r.rescue.snake && r.rescue.snake.name,
                r.district,
                r.user && r.user.name,
                r._id,
              ].filter(Boolean),
              data: r,
              icon: createMarkerIcon(MARKER_TYPES.SNAKE_REPORT, color),
            };
          });
          
          const locGroups = {};
          rawMarkers.forEach(m => {
            const key = `${m.lat},${m.lng}`;
            if (!locGroups[key]) locGroups[key] = [];
            locGroups[key].push(m);
          });
          
          const finalMarkers = [];
          Object.values(locGroups).forEach(group => {
            if (group.length === 1) {
              finalMarkers.push(group[0]);
            } else {
              const radius = 0.00015 + (group.length * 0.000005);
              group.forEach((m, i) => {
                const angle = (i / group.length) * 2 * Math.PI;
                m.lat += Math.cos(angle) * radius;
                m.lng += Math.sin(angle) * radius;
                finalMarkers.push(m);
              });
            }
          });
          return finalMarkers;
        })(),
      }));

      return layers;
    },

    mapStats() {
      return [
        { title: 'Total Users', value: this.usersCount, icon: 'mdi-account-group', color: STATUS_COLORS.USER },
        { title: 'Total Rescuers', value: this.rescuersCount, icon: 'mdi-shield-account', color: STATUS_COLORS.AVAILABLE },
        { title: 'Total Hospitals', value: this.hospitalsCount, icon: 'mdi-hospital-building', color: STATUS_COLORS.HOSPITAL },
        { title: 'Total Snake Reports', value: this.snakeReportsCount, icon: 'mdi-alert-circle-outline', color: STATUS_COLORS.PENDING },
      ];
    },
  },

  beforeMount() {
    this.getAllData();
  },

  methods: {
    async getAllData() {
      this.appLoading = true;
      try {
        const headers = { 'x-auth-token': localStorage.getItem('token') };

        // Fetch Users
        const usersReq = axios.get('/user/allusers/v1', { headers, params: { type: 'map' } });
        // Fetch Rescuers
        const rescuersReq = axios.get('/users/rescuers/all', { headers });
        // Fetch Hospitals
        const hospitalsReq = axios.get('/hospital/all', { headers });
        // Fetch Snake Reports
        const snakeReportsReq = axios.post('/location/find/v2', {}, { headers });

        const [usersRes, rescuersRes, hospitalsRes, snakeReportsRes] = await Promise.all([
          usersReq.catch(e => ({ data: { status: false, msg: e.message } })),
          rescuersReq.catch(e => ({ data: { status: false, msg: e.message } })),
          hospitalsReq.catch(e => ({ data: { status: false, msg: e.message } })),
          snakeReportsReq.catch(e => ({ data: { status: false, msg: e.message } }))
        ]);

        if (usersRes.data.status === true) {
          this.users = usersRes.data.data;
          this.usersCount = usersRes.data.count;
        }
        
        if (rescuersRes.data.status === true) {
          this.rescuers = rescuersRes.data.data;
          this.rescuersCount = this.rescuers.length;
        }
        
        if (hospitalsRes.data.status === true) {
          this.hospitals = hospitalsRes.data.data;
          this.hospitalsCount = this.hospitals.length;
        }

        if (snakeReportsRes.data.status === true) {
          this.snakeReports = snakeReportsRes.data.data.records || [];
          this.snakeReportsCount = this.snakeReports.length;
        }

      } catch (err) {
        this.ServerError = true;
        console.error(err);
      } finally {
        this.appLoading = false;
      }
    },

    onMarkerClick({ data }) {
      console.log('Marker clicked:', data);
    },
  },
};
</script>

<style scoped>
.full-screen-map-wrapper {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}
</style>
